<template>
    <div class="c-yn" :style="wrapStyle">
        <div :style="innerStyle">
            <div :style="headerStyle">
                <div :style="eyebrowStyle">{{ (getCurrentDealName || agencyName || '').toUpperCase() }}</div>
                <h1 :style="h1Style">
                    Did you invoice for any new services or one-time fees
                    <span :style="dealAccentStyle">without a new SOW</span>?
                </h1>
                <p :style="leadStyle">
                    For <strong :style="emphasisStyle">{{ getCurrentDealName }}</strong>.
                    Sometimes work happens without a fresh SOW — we still want to
                    capture the invoice. Let us know.
                </p>
            </div>

            <CCard :extra-style="cardStyle">
                <div :style="choicePairStyle">
                    <CChoice
                        icon="✓"
                        label="Yes, we did"
                        sub="I'll record those without a SOW"
                        tone="green"
                        :selected="isHasNewSow === true"
                        @click="isHasNewSow = true"
                    />
                    <CChoice
                        icon="–"
                        label="No, nothing extra"
                        sub="Move on to the next step"
                        tone="neutral"
                        :selected="isHasNewSow === false"
                        @click="isHasNewSow = false"
                    />
                </div>

                <div :style="infoBlockStyle">
                    <span :style="infoIconStyle">i</span>
                    <div>
                        <strong :style="emphasisStyle">"Without a SOW"</strong>
                        usually means: small upsells, last-minute one-time fees,
                        or work invoiced before the paperwork caught up. Saying
                        yes lets you log them on the next screen.
                    </div>
                </div>
            </CCard>

            <div :style="navStyle">
                <CBtn ghost @click="onPrevPage">← Previous</CBtn>
                <CBtn primary big @click="onNextPage">Next →</CBtn>
            </div>
        </div>
    </div>
</template>

<script>
import moment from "moment";
import { SECTIONS } from "../consts";
import { scrollToTop } from "../../../helpers/_scrollToTop";

import CCard from "../c-design/CCard.vue";
import CChoice from "../c-design/CChoice.vue";
import CBtn from "../c-design/CBtn.vue";
import viewportMixin from "../c-design/useViewport";
import { C_BG, C_INK, C_INK2, C_MUTED, C_HAIR, C_CREAM, C_RED, FONT_DISPLAY, navMobileStickyStyle } from "../c-design/tokens";

export default {
    name: "Wizard_Do_You_Have_New_Upsell",
    mixins: [viewportMixin],
    components: { CCard, CChoice, CBtn },
    props: ["onChangeSection"],
    data() {
        return {
            SECTIONS,
            isHasNewUpsell: null,
            isHasNewSow: null,
        };
    },

    mounted() {
        scrollToTop();
    },

    computed: {
        prevSection() {
            return this.$store.getters.getPrevComponent;
        },
        currentBrand() {
            return this.$store.getters.getCurrentBrandName;
        },
        agencyName() {
            return this.$store.getters.getAgencyName;
        },
        isEditUpsell() {
            return this.$store.getters.getIsEditUpsell;
        },
        getCurrentDealId() {
            return (
                this.$store.getters.getSalesPages(
                    this.$store.getters.getDealSalesIndex
                ).deal_id || null
            );
        },
        getCurrentDealName() {
            return (
                this.$store.getters.getSalesPages(
                    this.$store.getters.getDealSalesIndex
                ).deal_name || null
            );
        },
        sowIndex() {
            return this.$store.getters.getCurrentIndexSOW;
        },
        // ===== c-design style maps =====
        wrapStyle() {
            return {
                background: C_BG,
                minHeight: '70vh',
                padding: this.isMobile ? '14px 12px 110px' : '24px 16px 40px',
            };
        },
        innerStyle() {
            return { maxWidth: '780px', margin: '0 auto' };
        },
        headerStyle() {
            return {
                marginBottom: this.isMobile ? '16px' : '24px',
                padding: this.isMobile ? '0 4px' : '0 8px',
            };
        },
        eyebrowStyle() {
            return {
                fontSize: '12px', color: C_MUTED, letterSpacing: '0.6px',
                textTransform: 'uppercase', fontWeight: 600,
            };
        },
        h1Style() {
            return {
                margin: '6px 0 8px', fontFamily: FONT_DISPLAY,
                fontSize: this.isMobile ? '24px' : '26px',
                fontWeight: 700, letterSpacing: '-0.6px',
                lineHeight: 1.15, color: C_INK,
            };
        },
        dealAccentStyle() {
            return { color: C_RED };
        },
        leadStyle() {
            return { margin: 0, fontSize: '15px', color: C_MUTED, lineHeight: 1.55 };
        },
        infoBlockStyle() {
            return {
                marginTop: '18px', padding: '14px 18px',
                background: C_CREAM, borderRadius: '12px',
                display: 'flex', alignItems: 'flex-start', gap: '12px',
                fontSize: '13px', color: C_INK2, lineHeight: 1.55,
            };
        },
        infoIconStyle() {
            return {
                width: '22px', height: '22px', borderRadius: '50%',
                background: '#fff', border: `1px solid ${C_HAIR}`,
                color: C_INK2,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '12px', fontWeight: 700,
                flexShrink: 0,
            };
        },
        emphasisStyle() {
            return { color: C_INK, fontWeight: 600 };
        },
        cardStyle() {
            return {
                marginBottom: this.isMobile ? '14px' : '18px',
                padding: this.isMobile ? '22px' : '32px 36px',
            };
        },
        choicePairStyle() {
            return {
                display: 'grid',
                gridTemplateColumns: this.isMobile ? '1fr' : '1fr 1fr',
                gap: this.isMobile ? '10px' : '12px',
            };
        },
        navStyle() {
            return navMobileStickyStyle(this.isMobile, {
                marginTop: '8px', padding: '16px 24px',
                background: '#fff', borderRadius: '16px',
                border: `1px solid ${C_HAIR}`,
                display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', gap: '16px',
            });
        },
    },

    methods: {
        async getSOWTitle() {
            let __title = "";

            await axios
                .post("/api/wizard/getList", {
                    page: "new_sow",
                    deal_id: this.getCurrentDealId,
                    sow_index: this.isEditUpsell
                        ? this.sowIndex
                        : this.sowIndex + 1,
                })
                .then((res) => {
                    __title = res.data.page.sow_title;
                });

            return __title;
        },
        onNextPage() {
            if (this.isHasNewSow === undefined || this.isHasNewSow === null) {
                this.$toast.error("Please select an answer");
                return;
            }

            const salesPages = this.$store.getters.getSalesPages(
                this.$store.getters.getSalesPagesIndex + 1
            );

            if (this.isEditUpsell) {
                if (this.isHasNewUpsell) {
                    this.onChangeSection(
                        SECTIONS.SOW,
                        SECTIONS.DO_YOU_HAVE_UPSELL
                    );
                    return;
                } else {
                    this.onChangeSection(
                        SECTIONS.UPSELL,
                        SECTIONS.DO_YOU_HAVE_UPSELL
                    );
                }
                return;
            }

            if (this.isHasNewUpsell) {
                this.onChangeSection(SECTIONS.SOW, SECTIONS.DO_YOU_HAVE_UPSELL);
                return;
            }

            if (!this.isHasNewUpsell && this.isHasNewSow) {
                this.$store.commit("setIsWithoutSOW", true);
                this.onCreateSOWWithoutUpsell();
                return;
            }

            if (this.isHasNewSow) {
                this.$store.commit("setIsWithoutSOW", true);
                this.onCreateSOWWithoutUpsell();
                return;
            } else {
                if (salesPages) {
                    this.$store.commit(
                        "setSalesPagesIndex",
                        this.$store.getters.getSalesPagesIndex + 1
                    );

                    this.$store.commit("incrementDealIndex");
                    this.$store.commit("setSaleIndex", 0);

                    this.onChangeSection(
                        SECTIONS.EDIT_SALE_LINE_ITEM,
                        SECTIONS.DASHBOARD
                    );
                    return;
                } else {
                    this.onChangeSection(
                        SECTIONS.INVOICES,
                        SECTIONS.DO_YOU_HAVE_UPSELL
                    );
                }
            }
        },
        onPrevPage() {
            this.onChangeSection(
                SECTIONS.DO_YOU_HAVE_UPSELL,
                SECTIONS.DO_YOU_HAVE_SERVICE_WITHOUT_SOW
            );
        },

        async onCreateSOWWithoutUpsell() {
            let sow_title = (await this.getSOWTitle()) || "";

            let currentDate = moment();
            let pastMonthDate = currentDate.subtract(1, "months");
            pastMonthDate.startOf("month");
            let formattedDate = pastMonthDate.format("MM/DD/YYYY");

            const data = {
                name: sow_title,
                date: formattedDate,
                url: "",
                description: "I don’t have signed SOW",
                deal_id: this.getCurrentDealId,
                type: "none",
                is_file: false,
                valid_date: null,
                sow_index: this.isEditUpsell
                    ? this.sowIndex
                    : this.sowIndex + 1,
            };

            const formData = new FormData();

            formData.append("action", "createSow");
            formData.append("data", JSON.stringify(data));
            // formData.append("file", null);

            axios
                .post("api/wizard/updateDraft", formData, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                })
                .then(() => {
                    if (!this.isEditUpsell) {
                        this.$store.commit("setIndexSOW", this.sowIndex + 1);
                        this.$store.commit(
                            "setIndexUpsell",
                            this.upsellIndex + 1
                        );
                    }
                    this.onChangeSection(
                        SECTIONS.UPSELL,
                        SECTIONS.DO_YOU_HAVE_UPSELL
                    );
                })
                .catch((err) => {
                    this.$toast.error("Something went wrong!");
                    console.log("ERROR addSOWFile", err);
                });
        },
    },
};
</script>
